import { useState, type FormEvent } from "react";
import { Save } from "lucide-react";
import { Button } from "./ui";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import type { Task } from "../types";

const priorityLabels: Array<[number, string]> = [
  [3, "高优先级"],
  [2, "中优先级"],
  [1, "低优先级"]
];

export function EditTaskModal({ task, onClose }: { task: Task; onClose: () => void }) {
  const { mutate, refresh } = useApp();
  const [saving, setSaving] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    const form = new FormData(event.currentTarget);
    const payload = {
      title: String(form.get("title") ?? "").trim(),
      priority: Number(form.get("priority")),
      dueDate: String(form.get("dueDate") ?? "") || null,
      valueScore: Number(form.get("valueScore")),
      difficulty: Number(form.get("difficulty")),
      estimatedMinutes: form.get("estimatedMinutes") ? Number(form.get("estimatedMinutes")) : null,
      pinned: form.get("pinned") === "on",
      detail: String(form.get("detail") ?? "").trim()
    };
    if (!payload.title) {
      setSaving(false);
      return;
    }
    const saved = await mutate(() => api.update("tasks", task.id, payload), "任务已更新");
    setSaving(false);
    if (saved) {
      void refresh();
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-sheet" onClick={(event) => event.stopPropagation()}>
        <div className="modal__header">
          <div>
            <span className="eyebrow">EDIT TASK · 编辑任务</span>
            <h2>修改任务</h2>
            <p>改标题、优先级、截止日期或备注。</p>
          </div>
          <button className="modal__close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={(event) => void submit(event)}>
          <div className="modal__body form-grid">
            <label className="field field--full">
              <span>任务标题</span>
              <input name="title" defaultValue={task.title} required maxLength={120} />
            </label>
            <label className="field">
              <span>优先级</span>
              <select name="priority" defaultValue={task.priority}>
                {priorityLabels.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className="field">
              <span>截止日期</span>
              <input name="dueDate" type="date" defaultValue={task.due_date?.slice(0, 10) ?? ""} />
            </label>
            <label className="field">
              <span>价值分（1-5）</span>
              <input name="valueScore" type="number" min={1} max={5} defaultValue={task.value_score ?? 3} />
            </label>
            <label className="field">
              <span>难度（1-5）</span>
              <input name="difficulty" type="number" min={1} max={5} defaultValue={task.difficulty ?? 2} />
            </label>
            <label className="field">
              <span>预计分钟</span>
              <input name="estimatedMinutes" type="number" min={1} placeholder="可选" defaultValue={task.estimated_minutes ?? ""} />
            </label>
            <label className="field field--full">
              <span>详情</span>
              <textarea name="detail" rows={3} placeholder="补充信息（可选）" defaultValue={task.detail ?? ""} />
            </label>
            <label className="check-field field--full">
              <input name="pinned" type="checkbox" defaultChecked={Boolean(task.pinned)} />
              <span>置顶显示</span>
            </label>
          </div>
          <div className="modal__footer">
            <Button type="button" variant="ghost" onClick={onClose}>取消</Button>
            <Button type="submit" icon={<Save size={15} />} disabled={saving}>{saving ? "保存中…" : "保存修改"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
